import React from 'react';
import { TrendingUp, TrendingDown, RefreshCw } from 'lucide-react';

interface MarketIndicatorProps {
  label: string;
  value: string;
  change: number | undefined;
  isLoading?: boolean;
  bgColor: string;
  textColor: string;
  icon?: React.ReactNode;
}

const MarketIndicator: React.FC<MarketIndicatorProps> = ({
  label,
  value,
  change,
  isLoading = false,
  bgColor,
  textColor,
  icon
}) => {
  // Ensure change is a number
  const numChange = typeof change === 'number' ? change : 0;
  const isPositive = numChange > 0;
  const isNegative = numChange < 0;

  const formatChange = (changeValue: number) => {
    const sign = changeValue > 0 ? '+' : '';
    return `${sign}${changeValue.toFixed(2)}`;
  };

  const getChangeColor = () => {
    if (isPositive) return 'text-green-600';
    if (isNegative) return 'text-red-600';
    return 'text-gray-600';
  };

  const getChangeIcon = () => {
    if (isPositive) return <TrendingUp className="w-4 h-4" aria-hidden="true" />;
    if (isNegative) return <TrendingDown className="w-4 h-4" aria-hidden="true" />;
    return null;
  };

  return (
    <div className={`flex justify-between items-center p-3 ${bgColor} rounded-lg transition-all duration-300`}>
      <div className="flex items-center space-x-2">
        {icon && <div className="text-gray-600">{icon}</div>}
        <span className="text-gray-700 font-medium">{label}</span>
      </div>

      <div className="flex items-center space-x-2">
        {isLoading ? (
          <RefreshCw className="w-4 h-4 animate-spin text-gray-400" />
        ) : (
          <>
            <span className={`font-bold ${textColor}`}>{value}</span>
            {numChange !== 0 && (
              <div className={`flex items-center space-x-1 ${getChangeColor()}`}>
                {getChangeIcon()}
                <span className="text-sm font-medium">
                  {formatChange(numChange)}
                </span>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MarketIndicator;
